import { useAppDispatch } from "@grassrootly/rn-core";
import { MainStackScreenProps } from '../../navigation/Navigation.types';
import { GetPlayerFeaturedGame, GetPlayerGroups, GetPlayerPreviousGames, GetPlayerPreviousGamesPayload, IPlayerGame, IPlayerGroup, UpdateAttendance, GetGameViewById, GetLatestGames, ILatestGame, GetPlayerMonthlyForm, PlayerMonthlyFormRow } from '@grassrootly/api';
import { useFeaturedGame, usePreviousGames } from '../../api/game/GameSelector';
import useBaseRequest from '../../api/BaseRequest';
import { gameActions } from '../../api/game/GameReducer';
import { gameViewActions } from '../../api/game/GameViewReducer';
import { useFocusEffect } from '@react-navigation/native';
import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../components/guard/useAuth';
import moment from 'moment';
import { groupsActions } from '../../api/groups/GroupsReducer';
import { usePlayerGroups } from '../../api/groups/GroupsSelector';

export interface IHomeScreenHook {
  previousGames: IPlayerGame[],
  getPreviousGames: (params: GetPlayerPreviousGamesPayload) => void,
  loadingPreviousGames: boolean,
  loadMorePreviousGames: () => void,
  loadingMorePreviousGames: boolean,
  hasMorePreviousGames: boolean,
  setGameAttendancy: ({ gameId, attending }: { gameId: number, attending: boolean }) => void
  updateAttendancyInProgress: boolean,
  groups: IPlayerGroup[] | null,
  isMemberOfAnyGroup: boolean,
  isAdminOfAnyGroup: boolean,
  openGameScreen: (gameId: number) => void,
  featuredGame: IPlayerGame | null,
  loadingFeaturedGame: boolean,
  latestMatches: ILatestGame[],
  loadingLatestMatches: boolean,
  playerMonthlyForm: PlayerMonthlyFormRow[],
  loadingPlayerMonthlyForm: boolean,
};

export function useHome({ route, navigation }: MainStackScreenProps<"home_remastered">): IHomeScreenHook {
  const dispatch = useAppDispatch();

  const [isMemberOfAnyGroup, setIsMemberOfAnyGroup] = useState<boolean>(false);
  const [isAdminOfAnyGroup, setIsAdminOfAnyGroup] = useState<boolean>(false);
  const [previousGamesOffset, setPreviousGamesOffset] = useState<number>(0);
  const [hasMorePreviousGames, setHasMorePreviousGames] = useState<boolean>(true);
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);
  const [latestMatches, setLatestMatches] = useState<ILatestGame[]>([]);
  const [playerMonthlyForm, setPlayerMonthlyForm] = useState<PlayerMonthlyFormRow[]>([]);

  // Hook to the featured game from the store
  const featuredGame = useFeaturedGame();
  // Hook to the current player (logedin user)
  const { user } = useAuth();
  // Hook to the player previous games list
  const previousGames = usePreviousGames();
  // Hook to the player groups from the store
  const groups = usePlayerGroups();

  // Refresh screen data whenever screen gets focused and/or player is changed
  useFocusEffect(useCallback(() => {
    if (user) {
      const now = moment().toISOString();
      getFeaturedGame({ pid: user.id });
      // Reset pagination and load first page
      setPreviousGamesOffset(0);
      setHasMorePreviousGames(true);
      setIsInitialLoad(true);
      dispatch(gameActions.setPreviousGames([])); // Clear existing games
      getPreviousGames({ date_to: now, pid: user.id, limit: 10, offset: 0 });
      getGroups({ playerId: user.id });
      loadPlayerMonthlyForm({ pid: user.id });
    }
  }, [user, dispatch]));

  // Whenever groups list has changed, reevaluate membership and administration
  useEffect(() => {
    setIsMemberOfAnyGroup(groups && !!groups.length);
    setIsAdminOfAnyGroup(groups && !!groups.find((x) => x.is_owner));
  }, [groups]);

  // Get game view request (for populating gameView slice with voting data)
  const { execute: getGameView } = useBaseRequest(GetGameViewById, {
    onCompleted: (data) => {
      if (data) {
        dispatch(gameViewActions.setGameView(data));
      }
    }
  });

  // Get feature game request (get feature game from API and dispatch it in the store)
  const { loading: loadingFeaturedGame, execute: getFeaturedGame } = useBaseRequest(GetPlayerFeaturedGame, {
    onCompleted: (data) => {
      dispatch(gameActions.setFeaturedGame(data));
      // Also fetch game view data so vote button works immediately
      if (data) {
        getGameView({ id: data.game_id });
      }
    }
  });

  // Get previous games request (get previous games from the API and dispatch it to the store)
  const { loading: loadingPreviousGames, execute: getPreviousGamesRequest } = useBaseRequest(GetPlayerPreviousGames, {
    onCompleted: (data) => {
      if (isInitialLoad) {
        // First load - replace all games
        dispatch(gameActions.setPreviousGames(data));
        setPreviousGamesOffset(data.length); // Set offset to number of items loaded
        setIsInitialLoad(false);
      } else {
        // Pagination load - append games
        dispatch(gameActions.setPreviousGames([...previousGames, ...data]));
        setPreviousGamesOffset(prev => prev + data.length); // Increment offset by items loaded
      }
      
      setHasMorePreviousGames(data.length === 10); // If we got less than 10, no more games
    }
  });

  // Use the same request handler for both initial load and pagination
  const getPreviousGames = getPreviousGamesRequest;
  const loadingMorePreviousGames = loadingPreviousGames;

  // Latest games on Eqipa (global, from games_view)
  const { loading: loadingLatestMatches, execute: loadLatestGames } = useBaseRequest(GetLatestGames, {
    onCompleted: (data: ILatestGame[]) => {
      setLatestMatches(data || []);
    },
  });

  // Load latest games once when Home screen is mounted
  useEffect(() => {
    loadLatestGames({} as any);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Player monthly form: last 3 full months of stats
  const { loading: loadingPlayerMonthlyForm, execute: loadPlayerMonthlyForm } = useBaseRequest(GetPlayerMonthlyForm, {
    onCompleted: (data: PlayerMonthlyFormRow[]) => {
      setPlayerMonthlyForm(data || []);
    },
  });

  // Set game attendancy request (call API to update game attendancy)
  const { loading: updateAttendancyInProgress, execute: setGameAttendancy } = useBaseRequest(async ({ gameId, attending }: { gameId: number, attending: boolean }) => {
    await UpdateAttendance({ id: gameId, attending, playerId: (user?.id as number) });
    return GetPlayerFeaturedGame({ pid: user?.id });
  }, {
    onCompleted: (data: IPlayerGame | null) => {
      dispatch(gameActions.setFeaturedGame(data));
      // Also refresh game view data to update attendance status
      if (data) {
        getGameView({ id: data.game_id });
      }
    }
  });

  // Get player groups request
  const { execute: getGroups, loading } = useBaseRequest(GetPlayerGroups, {
    onCompleted: (data) => {
      // Always clear existing groups first to prevent stale data from previous users
      dispatch(groupsActions.cleanState());
      
      if (data && data.length) {
        dispatch(groupsActions.setPlayerGroups(data));
      }
    },
  });

  // Redirect user to game screen
  const openGameScreen = (gameId: number) => {
    navigation?.navigate("game", { id: gameId });
  };

  // Function to load more previous games
  const handleLoadMorePreviousGames = () => {
    if (user && hasMorePreviousGames && !loadingMorePreviousGames) {
      const now = moment().toISOString();
      setIsInitialLoad(false); // Ensure this is treated as pagination
      getPreviousGames({ 
        date_to: now, 
        pid: user.id, 
        limit: 10, 
        offset: previousGamesOffset 
      });
    }
  };

  return {
    previousGames,
    loadingPreviousGames,
    getPreviousGames,
    loadMorePreviousGames: handleLoadMorePreviousGames,
    loadingMorePreviousGames,
    hasMorePreviousGames,
    setGameAttendancy,
    updateAttendancyInProgress,
    groups,
    isMemberOfAnyGroup,
    isAdminOfAnyGroup,
    openGameScreen,
    featuredGame,
    loadingFeaturedGame,
    latestMatches,
    loadingLatestMatches,
    playerMonthlyForm,
    loadingPlayerMonthlyForm,
  };
}
